import {I18n} from 'aws-amplify/utils';

import {
    delegationState,
    DnsZone,
} from '../types';

// FNP-1191 CRUD half — THE CLIENT-SIDE DELETE REFUSALS.
//
// ⚠️ TWO GUARDS LIVE IN TWO DIFFERENT PLACES, AND THAT IS DELIBERATE.
//
//   ZONE_NOT_EMPTY — the PROVIDER's refusal. Not checked here at all. Route 53
//   is the authority on whether a zone still holds records, and FNP-1219
//   explicitly declined to pre-check it even server-side: a local check
//   duplicates a rule the provider owns, costs another network call, and can
//   DISAGREE with the provider when a record is added between the check and the
//   delete. It surfaces as a classified error after the attempt.
//
//   DELEGATION — checked HERE, before the attempt. The provider has no opinion
//   about it: a delegated zone deletes perfectly happily at Route 53, and the
//   damage is entirely on Flexi's side of the boundary. Waiting for a refusal
//   that will never come would mean the zone is simply gone.
//
// So this file is not "the other half of the same check". It is the guard for
// the failure mode the provider will NOT protect anyone from.

export type ZoneDeletionBlock = {
    // Machine-readable so the modal branches rather than string-matching.
    reason: 'DELEGATED' | 'DELEGATION_UNDETERMINED',
    title: string,
    // Why deleting this zone would hurt, in terms of what breaks.
    explanation: string,
    // ⚠️ Whether an operator may proceed anyway.
    //
    // A DELEGATED zone is a HARD stop: live traffic resolves through it.
    // An UNDETERMINED one is a SOFT stop — "we could not tell" is not evidence
    // of danger, and refusing outright would make undetermined zones
    // permanently undeletable through the UI, which is worse than the risk.
    overridable: boolean,
}

/**
 * Whether deleting this zone should be refused or warned about, on the
 * evidence Flexi holds locally.
 *
 * Returns `undefined` when there is no local objection — which is NOT a promise
 * that the delete will succeed. The provider still owns ZONE_NOT_EMPTY.
 */
export const zoneDeletionBlock = (zone?: DnsZone): ZoneDeletionBlock | undefined => {

    if (!zone) {
        return undefined;
    }

    // Branched through delegationState(), never the raw boolean. `false` and
    // `null` are DIFFERENT answers and the whole read half is built on not
    // collapsing them — a `!zone.appearsDelegated` test here would treat "not
    // determined" as "safe to delete", which is the exact inversion of the
    // truth: undetermined is the case we know least about.
    const state = delegationState(zone);

    if (state === 'DELEGATED') {
        return {
            reason: 'DELEGATED',
            title: I18n.get('This zone is live'),
            explanation: I18n.get('The registrar nameservers point at this provider, so this zone is answering DNS for real traffic right now. Deleting it takes every hostname in it offline immediately, and the records cannot be recovered from Flexi. Repoint the domain at its new nameservers first, and delete this zone once it is no longer authoritative.'),
            // A hard stop. Not a confirmation an operator can click through in
            // the same gesture that opened the modal.
            overridable: false,
        };
    }

    if (state === 'UNDETERMINED') {
        return {
            reason: 'DELEGATION_UNDETERMINED',
            title: I18n.get('Delegation could not be determined'),
            // ⚠️ States the LIMIT of what is known rather than implying danger.
            // `appearsDelegated` is a read of the provider's published
            // nameservers, not a live resolver query, so null legitimately
            // happens in the minutes after a delegation change.
            explanation: I18n.get('Flexi could not tell whether this zone is serving live traffic — this is not the same as knowing it is idle. If the domain still points here, deleting the zone takes it offline. Check the domain\'s nameservers at its registrar before continuing.'),
            overridable: true,
        };
    }

    // NOT_DELEGATED. Determined, and the registrar does not point here — the
    // one case with positive evidence that deleting is safe from Flexi's side.
    return undefined;
};
